import React from 'react';
import { cn } from './ui/utils';
import {
  LayoutDashboard, FolderKanban, FileText, Bell, BarChart3,
  ChevronLeft, ChevronRight, Upload, Tag, Briefcase, Users,
} from 'lucide-react';
import Logo from '../assets/Logo.png';
import { Button } from './ui/button';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';
import telanganaLogo from '../assets/31915afc7cedf02b706764d99a1a44d8c00050bc.png';
import awsIntelLogo from '../assets/101b00e80dc3b525c116d33b93a96aa7df1a0617.png';
import startupindiaLogo from '../assets/dd8d76a6b89bd80b4a16bb8570285b6a0d202afb.png';
import xynergyLogo from '../assets/ee38d279dc16a25dbb9bd122fdd16531803e567e.png';
import { usePermission, useMe, useActiveClient } from '../hooks/useMe';

// Each item declares an optional `permission` — items with a permission the
// user lacks are hidden.  Items with `superAdminOnly: true` are visible only
// when the current user is a platform super-admin (regardless of permission).
// Pages without either are shown to anyone authenticated.
const SYSTEM_ITEMS = [
  { id: 'dashboard',         label: 'Dashboard',        icon: LayoutDashboard, permission: null },
  { id: 'projects',          label: 'Projects',         icon: FolderKanban,    permission: 'project:read' },
  { id: 'reports',           label: 'Reports',          icon: FileText,        permission: 'report:read' },
  { id: 'alerts',            label: 'Alerts',           icon: Bell,            permission: 'notification:read' },
  { id: 'analytics',         label: 'Analytics',        icon: BarChart3,       permission: 'analytics:read' },
  // Pilot / Annotator data ingestion screens are operations-only — exposed
  // to super-admins, hidden from client_admin / client_user roles.
  { id: 'pilot-upload',      label: 'Pilot Upload',     icon: Upload,          permission: 'file:upload', superAdminOnly: true },
  { id: 'annotator-upload',  label: 'Annotator Upload', icon: Tag,             permission: 'file:upload', superAdminOnly: true },
  // Tenant-scoped user management — client_admin sees this for their own
  // members. Super-admin also gets it (their permission set is a superset).
  { id: 'users',             label: 'Users',            icon: Users,           permission: 'user:invite' },
  { id: 'admin-clients',     label: 'Clients',          icon: Briefcase,       permission: 'client:create' },
];

const recognizedLogos = [telanganaLogo, awsIntelLogo, startupindiaLogo];

export function Sidebar({
  activeSection, onSectionChange, collapsed, onToggleCollapse,
  // onProjectSelect kept in the prop signature for backward compatibility but
  // no longer used here — projects are selected from the Projects page itself.
  onProjectSelect,  // eslint-disable-line no-unused-vars
}) {
  const me = useMe();
  const activeClient = useActiveClient();
  // Client portals show the active client's logo in the top-left; super-
  // admins keep the platform logo (they switch tenants from the Header
  // dropdown rather than living inside one client).
  const showClientBranding = !me?.is_super_admin && activeClient;
  const [logoBroken, setLogoBroken] = React.useState(false);
  React.useEffect(() => { setLogoBroken(false); }, [activeClient?.id]);

  return (
    <TooltipProvider>
      <div
        className={cn(
          'fixed left-0 top-0 h-full bg-sidebar border-r border-sidebar-border transition-all duration-300 z-50 flex flex-col',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Header */}
        <div className="border-b border-sidebar-border">
          <div className="flex items-center justify-between p-4">
            {!collapsed && (
              <div
                className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => onSectionChange('dashboard')}
              >
                <div
                  className="flex items-center justify-center overflow-hidden rounded-lg bg-white/5 border border-white/5"
                  // Inline px sizes so a giant logo PNG can't stretch the
                  // sidebar header even before CSS lays out the image.
                  style={{ width: 40, height: 40, minWidth: 40, flex: '0 0 auto' }}
                >
                  {showClientBranding && activeClient?.logo_url && !logoBroken ? (
                    <img
                      src={activeClient.logo_url}
                      alt={`${activeClient.name} logo`}
                      width={40}
                      height={40}
                      style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
                      className="block object-contain"
                      onError={() => setLogoBroken(true)}
                    />
                  ) : (
                    <img
                      src={Logo}
                      alt="Akin Logo"
                      width={40}
                      height={40}
                      style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
                      className="block object-contain"
                    />
                  )}
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-sidebar-foreground truncate">
                    {showClientBranding ? activeClient.name : 'Akin Analytics'}
                  </h1>
                  <p className="text-xs text-sidebar-foreground/60">
                    {showClientBranding ? `Client · ${activeClient.code}` : 'Enterprise Platform'}
                  </p>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="text-sidebar-foreground hover:bg-sidebar-accent"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Navigation — system items only */}
        <nav className="flex-1 overflow-y-auto p-3">
          <div className="space-y-1">
            {SYSTEM_ITEMS.map((item) => (
              <SystemItem
                key={item.id}
                item={item}
                active={activeSection === item.id}
                onClick={() => onSectionChange(item.id)}
                collapsed={collapsed}
              />
            ))}
          </div>
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div className="border-t border-sidebar-border">
            <div className="px-4 py-4 border-b border-sidebar-border/50">
              <div className="flex flex-col items-center space-y-2">
                <div className="text-xs text-sidebar-foreground/60">Powered By</div>
                <img src={xynergyLogo} alt="XYNERGY Logo" className="w-full h-auto object-contain px-2" />
              </div>
            </div>
            <div className="px-4 py-4 overflow-hidden">
              <div className="text-xs text-sidebar-foreground/60 text-center mb-2">Recognized By</div>
              <div className="flex items-center space-x-4 animate-scroll">
                {[...recognizedLogos, ...recognizedLogos].map((logo, index) => (
                  <img key={index} src={logo} alt="Partner logo" className="h-8 object-contain flex-shrink-0" />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll { animation: scroll 15s linear infinite; }
        .animate-scroll:hover { animation-play-state: paused; }
      `}</style>
    </TooltipProvider>
  );
}

function SystemItem({ item, active, onClick, collapsed }) {
  const me = useMe();
  const allowed = usePermission(item.permission || 'always');
  // Super-admin-gated items are hidden from regular client roles even if
  // they happen to hold the underlying permission.
  if (item.superAdminOnly && !me?.is_super_admin) return null;
  const visible = item.permission == null ? true : allowed;
  if (!visible) return null;

  const Icon = item.icon;
  const button = (
    <Button
      variant={active ? 'default' : 'ghost'}
      className={cn(
        'w-full justify-start gap-3 h-11 transition-all duration-200',
        collapsed && 'justify-center px-2',
        active
          ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      )}
      onClick={onClick}
    >
      <Icon className="w-5 h-5" />
      {!collapsed && <span className="font-medium">{item.label}</span>}
    </Button>
  );

  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        <p>{item.label}</p>
      </TooltipContent>
    </Tooltip>
  );
}
